import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import React, { useEffect, useState, useRef } from 'react';
import { chartColorPallet as DEFAULT_COLORS } from '@/config/colorPallets/universalDesignSystem';
import useSelections from '@/components/widget/misc/useSelections';
import { selectEditingArea, getEditingAreaAttr } from '@/components/Editor/hooks/useEditingAreas';
import { useTheme } from '@/components/widget/Layout/ThemeProvider';
import { hasAnySelectionData, hasSelections } from '../../utils/selections';
import { buildChartRenderingHash } from '../internal/utils';
import useData from './useData';
import useSubscribeWidgetResults from '../hooks/useSubscribeWidgetResults';
import settings, { selectionConfig } from './settings';
import Header from '../misc/Header';
import StyleVariables, { getSectionAttr, getSectionAttrSelector } from '../../misc/StyleVariables';
import styles from './styles.module.scss';
import Loading from './Loading';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const TreeMap = _ref => {
  let {
    widgetId,
    sectionId,
    reportingWidgetId,
    elementId,
    editing = false,
    config,
    isLargeContainer
  } = _ref;
  const {
    colorPallet: defaultColorPallet = DEFAULT_COLORS
  } = useTheme();
  const {
    selections = '',
    chartTitle,
    // selectionsStrategy = '',
    selectionsLabeled = false,
    seriesOptions = [],
    colorPallet = defaultColorPallet,
    animation = {}
  } = config;
  const canvasElement = useRef(null);
  const {
    0: chart,
    1: setChart
  } = useState(null);
  const {
    0: isEmpty,
    1: setIsEmpty
  } = useState(true);
  const isLarge = isLargeContainer;
  const results = useSubscribeWidgetResults(reportingWidgetId); // Get relevant results for each selection group

  const {
    selectionResults
  } = useSelections({
    results,
    selections,
    selectionConfig,
    selectionsLabeled
  }); // Data & Loading States

  const hasNoData = !hasAnySelectionData(selectionResults);
  const isLoading = !results.length;
  const needSelectionEditing = editing && !hasSelections(selections); // Combine results with rendering styles

  const {
    data
  } = useData(selectionResults, seriesOptions, colorPallet);
  const chartConfigHash = `${chartTitle}` + buildChartRenderingHash({
    animation
  });
  const redrawIndex = data.filter(_ref2 => {
    let {
      value
    } = _ref2;
    return Boolean(value);
  }).map(_ref3 => {
    let {
      id,
      name,
      compiledTemplate: ct,
      selectionIndex: sl,
      value,
      color
    } = _ref3;
    return [id, name, ct, sl, value, color].map(a => `${a || ''}`).join('');
  }).join('') + chartConfigHash;
  useEffect(() => {
    const hasDrawableData = Boolean(redrawIndex);
    if (!canvasElement.current || !hasDrawableData) return; // Start replacing previous
    // highcharts instance
    // NOTE: updating chart series data
    // is not possible due to Highcharts issues

    if (chart) {
      try {
        canvasElement.current.style.height = `${chart.container.offsetHeight}px`;
        chart.destroy();
      } catch (err) {} // eslint-disable-line

    }

    setIsEmpty(false); // Render highchart

    requestAnimationFrame(async () => {
      const {
        default: Highcharts
      } = await import('highcharts');
      const {
        default: Treemap
      } = await import('highcharts/modules/treemap');
      setupHcTreemapModule(Highcharts, Treemap);
      Highcharts.setOptions({
        lang: {
          thousandsSep: ','
        }
      });
      const isAnimationEnabled = (animation === null || animation === void 0 ? void 0 : animation.enabled) ?? true; // @ts-ignore

      const treeMap = Highcharts.chart(canvasElement.current, {
        chart: {
          margin: [0, 0, 0, 0] // remove extra background padding

        },
        colorAxis: {
          minColor: '#FFFFFF',
          maxColor: (Highcharts.getOptions().colors || [])[0]
        },
        series: [{
          type: 'treemap',
          layoutAlgorithm: 'stripes',
          alternateStartingDirection: true,
          data,
          animation: isAnimationEnabled ? {
            defer: animation.delay,
            duration: animation.duration,
            easing: animation.easing
          } : false
        }],
        tooltip: {
          pointFormatter: function () {
            // @ts-ignore
            return `<b>${this.options.name}</b>: ${this.options.compiledTemplate}<br/>`;
          }
        },
        title: {
          text: chartTitle || ''
        },
        plotOptions: {
          series: {
            events: editing ? {
              click: evt => setItemAsEditingArea({
                widgetId,
                sectionId
              }, evt)
            } : undefined
          }
        }
      });
      setChart(treeMap); // @ts-ignore

      delete canvasElement.current.style.height; // remove tmp height
    }); // Chart teardown

    return () => {
      if (chart) {
        try {
          chart.destroy();
          setIsEmpty(true);
        } catch (err) {} // eslint-disable-line

      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasElement.current, redrawIndex]);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, elementId && {
    id: elementId
  }), {}, {
    className: clsx(styles.container, isLarge ? styles['container--large'] : '')
  }, getSectionAttr(widgetId, sectionId)), needSelectionEditing ? {
    [getEditingAreaAttr()]: 'na'
  } : null), {}, {
    children: [/*#__PURE__*/_jsx(Header, {
      editing: editing,
      widgetId: widgetId,
      sectionId: sectionId,
      config: config,
      className: styles.header
    }), isEmpty || isLoading || hasNoData ? /*#__PURE__*/_jsx(Loading, {}) : null, /*#__PURE__*/_jsx("div", {
      // @ts-ignore
      ref: canvasElement,
      className: clsx(styles.canvas, {
        [styles['canvas--hide']]: isEmpty || isLoading || hasNoData
      })
    }), /*#__PURE__*/_jsx(StyleVariables, {
      selector: getSectionAttrSelector(widgetId, sectionId),
      data: _objectSpread(_objectSpread({}, settings.styles), config.styles || {})
    })]
  }));
};

export default TreeMap;
export { settings, selectionConfig, DEFAULT_COLORS };

function setupHcTreemapModule(Highcharts, Treemap) {
  const supportsTreemaps = Boolean( // @ts-ignore
  Object.keys(Highcharts._modules || {}).find(k => k.search('Treemap') > -1));
  !supportsTreemaps && Treemap(Highcharts);
} // Apply user selections of an individual
// chart item/block as the active editing area


function setItemAsEditingArea(_ref4, evt) {
  let {
    widgetId,
    sectionId
  } = _ref4;
  const point = evt.point || {};
  const options = point.options || {}; // @ts-ignore

  const selectionIndex = options.selectionIndex;
  if (typeof selectionIndex !== 'number') return; // @ts-ignore

  const clientX = evt.clientX; // @ts-ignore

  const clientY = evt.clientY;
  const hasClickPostion = typeof clientX === 'number' && typeof clientY === 'number'; // Emit edit area selection

  setTimeout(() => {
    selectEditingArea({
      widgetId,
      sectionId,
      sectionEditingScope: 'selections',
      sectionEditingArea: `item-${selectionIndex}`,
      clickPosition: hasClickPostion ? {
        x: clientX,
        y: clientY
      } : undefined,
      editingAreaMeta: JSON.stringify({
        // @ts-ignore
        selection: options.selection,
        selectionIndex
      })
    });
  });
}